import { useCallback, useLayoutEffect, useState } from 'react';

const KEY = 'simplici-theme';
const ATTR = 'data-sv2-theme';

/** Every mounted v2 page holds one claim on the attribute. */
let claims = 0;

/**
 * Every live instance of the hook, so a theme change made ANYWHERE lands in
 * all of them. This exists because the theme has more than one owner now: the
 * header's ThemeToggle and the Appearance card on General Settings both set
 * it, and each `useState` below is per-instance — without the broadcast, the
 * toggle would keep rendering (and flipping from) a stale value after the
 * Appearance card changed the theme under it.
 */
const instances = new Set<(t: 'light' | 'dark') => void>();

/**
 * Dark is the DEFAULT. Only an explicit stored 'light' opts out, so a first
 * visit — and a private window, where the read throws — both land on dark.
 * The stored value is still honoured either way, so anyone who has chosen
 * light keeps it.
 */
const read = (): 'light' | 'dark' => {
	try {
		return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark';
	} catch {
		return 'dark';
	}
};

const write = (theme: 'light' | 'dark') => {
	try {
		localStorage.setItem(KEY, theme);
	} catch {
		/* private mode — the toggle still works for this visit */
	}
};

/**
 * Set the sv2 theme from anywhere — hook or plain handler. Persists, flips
 * the attribute, and notifies every mounted instance.
 *
 * The Appearance card calls this alongside `themeModeState` (the PANEL-WIDE
 * `data-theme` store): that atom themes the legacy screens, this themes the
 * sv2 surfaces, and setting only the first is the bug where the segment
 * visibly did nothing on a redesigned page.
 */
export const setSv2Theme = (theme: 'light' | 'dark') => {
	const root = document.documentElement;
	if (theme === 'dark') root.setAttribute(ATTR, 'dark');
	else root.removeAttribute(ATTR);
	write(theme);
	instances.forEach(update => update(theme));
};

/**
 * Dark mode is a property of the redesigned surfaces, not of the whole panel.
 *
 * The attribute lives on <html> so the sidebar (rendered outside these
 * subtrees) can follow, and it is dropped only when the LAST v2 page unmounts —
 * a refcount, because navigating Dashboard → Sessions mounts the next page
 * before the previous one tears down, and a plain cleanup would strobe the
 * shell back to light in between. Leave the redesign entirely and every screen
 * still on the old styling renders exactly as it does today. The choice
 * persists, so coming back restores it.
 */
export const useSv2Theme = () => {
	const [theme, setTheme] = useState<'light' | 'dark'>(read);

	/*
	 * A LAYOUT EFFECT, like the one below, and that pairing is load-bearing.
	 *
	 * While this was passive and the one below was not, a route swap ran them in
	 * the wrong order: the arriving page's layout effect set the attribute, and
	 * only afterwards did the leaving page's passive cleanup run, drop the count
	 * to zero and REMOVE it again. Nothing put it back, because the new
	 * instance's `[theme]` effect had already run and its value had not changed.
	 * The result was a page that rendered light while the switch — reading the
	 * stored choice — still said dark. Reported going from log-in to sign-up.
	 *
	 * Both being layout effects, React runs every teardown before any setup in
	 * the same commit, so the count drops and the attribute is re-applied in the
	 * right order. The re-assert below makes that explicit rather than implied.
	 */
	useLayoutEffect(() => {
		claims += 1;
		instances.add(setTheme);
		/* A teardown in this same commit may have just cleared it. */
		const current = read();
		if (current === 'dark') document.documentElement.setAttribute(ATTR, 'dark');
		return () => {
			claims -= 1;
			instances.delete(setTheme);
			if (claims <= 0) document.documentElement.removeAttribute(ATTR);
		};
	}, []);

	/*
	 * BEFORE PAINT, not after. As a passive effect this ran after the browser
	 * could already have painted, so a surface mounting on a page that holds no
	 * claim — a dialog opening over a legacy route, which is the first screen a
	 * new account sees — flashed the light palette for a frame and then went
	 * dark. Layout effects run before paint, so the attribute is always in
	 * place by the time the tokens are resolved.
	 */
	useLayoutEffect(() => {
		const root = document.documentElement;
		if (theme === 'dark') root.setAttribute(ATTR, 'dark');
		else root.removeAttribute(ATTR);
		write(theme);
	}, [theme]);

	const toggle = useCallback(() => setSv2Theme(read() === 'dark' ? 'light' : 'dark'), []);

	return { theme, toggle, isDark: theme === 'dark' };
};

/** The Ops Deck shipped under the old name; keep it working. */
export const useOpsTheme = useSv2Theme;
